import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { checkInstallStatus, type InstallStatus } from '@/lib/check-install';

/**
 * Routes that are public even when system is not installed
 */
const PUBLIC_ROUTES = ['/', '/install', '/verify/certificate', '/maintenance'];

/**
 * Layout component that checks installation status and redirects
 * - API unreachable → /maintenance (system maintenance page)
 * - System not installed → /install (installation wizard)
 * - System installed → render child routes
 */
export function RequireInstall() {
  const [installStatus, setInstallStatus] = useState<InstallStatus | null>(null);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    async function checkStatus() {
      try {
        const status = await checkInstallStatus();
        if (mounted) {
          setInstallStatus(status);
        }
      } catch {
        if (mounted) {
          setInstallStatus('unreachable');
        }
      }
    }

    checkStatus();

    return () => {
      mounted = false;
    };
  }, []);

  // Show nothing while checking - prevents flash
  if (installStatus === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentPath = location.pathname;

  // If API is unreachable and not already on maintenance page, redirect to /maintenance
  if (installStatus === 'unreachable' && currentPath !== '/maintenance') {
    return <Navigate to="/maintenance" replace />;
  }

  // If not installed and trying to access non-public route, redirect to /install
  if (installStatus === 'not_installed' && !PUBLIC_ROUTES.includes(currentPath)) {
    return <Navigate to="/install" replace />;
  }

  return <Outlet />;
}

/**
 * Hook to check if system is installed
 * Use this in components that need to conditionally render based on install status
 */
export function useInstallStatus() {
  const [installStatus, setInstallStatus] = useState<InstallStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function checkStatus() {
      try {
        const status = await checkInstallStatus();
        if (mounted) {
          setInstallStatus(status);
        }
      } catch {
        if (mounted) {
          setInstallStatus('unreachable');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    checkStatus();

    return () => {
      mounted = false;
    };
  }, []);

  const isInstalled = installStatus === 'installed';
  return { isInstalled, loading, installStatus };
}
